/**
 * Stripe Service
 * Handles client-side Stripe operations including payment method creation
 */

import { loadStripe } from '@stripe/stripe-js';
import apiClient from './apiClient';

let stripePromise = null;
let stripePublishableKey = null;

/**
 * Get Stripe publishable key from backend or environment
 */
const getStripePublishableKey = async () => {
  if (stripePublishableKey) {
    return stripePublishableKey;
  }

  // Preferred: Try to get from backend first (production approach)
  try {
    console.log('🔍 Fetching Stripe configuration from backend...');
    const response = await apiClient.get('/api/billing/config');
    
    if (response.data.success && response.data.data && response.data.data.stripe && response.data.data.stripe.publishable_key) {
      stripePublishableKey = response.data.data.stripe.publishable_key;
      console.log('✅ Using Stripe publishable key from backend configuration');
      return stripePublishableKey;
    }
    console.warn('⚠️ Backend billing config response does not contain Stripe publishable key');
  } catch (error) {
    console.warn('⚠️ Failed to get Stripe configuration from backend:', error.message);
  }

  // Fallback: Use environment variable for development
  const envKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (envKey && envKey !== 'YOUR_STRIPE_TEST_PUBLISHABLE_KEY_HERE' && (envKey.startsWith('pk_test_') || envKey.startsWith('pk_live_'))) {
    stripePublishableKey = envKey;
    console.log('📝 Using Stripe publishable key from environment variable (development fallback)');
    return stripePublishableKey;
  }

  // Show helpful error message
  const errorMessage = `
🔧 Stripe Configuration Required

No valid Stripe publishable key found. Please configure one of:

1. **Backend Configuration (Recommended for Production):**
   - Deploy backend with STRIPE_PUBLISHABLE_KEY in AWS Secrets Manager
   - Backend /api/billing/config endpoint will provide the key

2. **Environment Variable (Development):**
   - Get your key from: https://dashboard.stripe.com/test/apikeys
   - Add to .env file: REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_test_your_key_here
   - Restart development server

Current environment key: ${envKey || 'Not set'}
  `;
  
  throw new Error(errorMessage);
};

/**
 * Initialize Stripe
 */
const getStripe = async () => {
  if (!stripePromise) {
    const publishableKey = await getStripePublishableKey();
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

/**
 * Create payment method using Stripe Elements (PCI compliant)
 * Note: This requires a card element from Stripe Elements and the Stripe instance from useStripe()
 */
export const createPaymentMethodFromElements = async (stripe, cardElement, billingDetails) => {
  try {
    if (!stripe) {
      throw new Error('Stripe is not initialized. Please ensure Stripe Elements provider is properly configured.');
    }

    if (!cardElement) {
      throw new Error('Card element is required for payment method creation');
    }

    // Create payment method with the provided Stripe instance (same instance used by Elements)
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: billingDetails,
    });

    if (error) {
      console.error('Stripe payment method creation error:', error);
      throw new Error(error.message);
    }

    console.log('✅ Stripe payment method created successfully:', paymentMethod.id);
    return {
      success: true,
      payment_method_id: paymentMethod.id,
      payment_method: paymentMethod
    };
  } catch (error) {
    console.error('Stripe payment method creation failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * DEPRECATED: Create payment method using raw card data
 * This method is deprecated and will not work with current Stripe security requirements
 */
export const createPaymentMethod = async (cardData, billingDetails) => {
  console.error('❌ DEPRECATED: Raw card data payment method creation is no longer supported by Stripe');
  console.error('Please use Stripe Elements instead: https://stripe.com/docs/stripe-js#elements');
  
  return {
    success: false,
    error: 'Raw card data payment method creation is deprecated. Please use Stripe Elements to collect card details: https://stripe.com/docs/stripe-js#elements'
  };
};

/**
 * Send payment method token to backend
 */
export const submitPaymentMethodToBackend = async (paymentMethodId, billingDetails) => {
  try {
    const response = await apiClient.post('/api/create-payment-method', {
      payment_method_id: paymentMethodId,
      billing_details: billingDetails,
    });

    return response.data;
  } catch (error) {
    console.error('Backend payment method submission failed:', error);
    throw error;
  }
};

/**
 * Complete payment method creation flow using Stripe Elements
 * 1. Create payment method with Stripe Elements (client-side)
 * 2. Send token to backend for processing
 * @param {Object} stripe - The Stripe instance from useStripe() hook
 * @param {Object} cardElement - The CardElement from Stripe Elements
 * @param {Object} billingDetails - Billing information
 */
export const createAndSubmitPaymentMethodFromElements = async (stripe, cardElement, billingDetails) => {
  try {
    // Step 1: Create payment method with Stripe Elements (using provided Stripe instance)
    const stripeResult = await createPaymentMethodFromElements(stripe, cardElement, billingDetails);
    
    if (!stripeResult.success) {
      throw new Error(stripeResult.error);
    }

    // Step 2: Send token to backend
    const backendResult = await submitPaymentMethodToBackend(
      stripeResult.payment_method_id,
      billingDetails
    );

    return {
      success: true,
      payment_method_id: stripeResult.payment_method_id,
      ...backendResult
    };
  } catch (error) {
    console.error('Payment method creation flow failed:', error);
    throw error;
  }
};

/**
 * DEPRECATED: Complete payment method creation flow using raw card data
 * This method is deprecated and will not work with current Stripe security requirements
 */
export const createAndSubmitPaymentMethod = async (cardData, billingDetails) => {
  console.error('❌ DEPRECATED: Raw card data payment method creation is no longer supported');
  console.error('Please use createAndSubmitPaymentMethodFromElements with Stripe Elements instead');
  
  throw new Error('Raw card data payment method creation is deprecated. Please use Stripe Elements to collect card details.');
};

const stripeService = {
  // New Stripe Elements methods (recommended)
  createPaymentMethodFromElements,
  createAndSubmitPaymentMethodFromElements,

  // Deprecated methods (for backwards compatibility)
  createPaymentMethod,
  createAndSubmitPaymentMethod,
  
  // Utility methods
  submitPaymentMethodToBackend,
  getStripe
};

export default stripeService;